import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { pdfActivityService } from '../services/pdfActivityService';
import { dailyActivityService } from '../services/dailyActivityService';
import BackButton from '../components/BackButton';
import { FaUpload, FaEdit, FaTrash, FaPlus, FaFilePdf, FaEye, FaDownload } from 'react-icons/fa';

const AdminActivityManager = () => {
  const { user, isAuthenticated } = useAuth();
  const [activities, setActivities] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [extractedActivities, setExtractedActivities] = useState([]);
  const [showExtractedPreview, setShowExtractedPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    console.log('🔐 Auth state:', { user: !!user, isAuthenticated, uid: user?.uid });
    loadActivities();
  }, [user, isAuthenticated]);

  const loadActivities = async () => {
    try {
      console.log('🔄 Loading activities from Firestore...');
      const activitiesData = await pdfActivityService.getActivitiesFromFirestore();
      setActivities(activitiesData);
      console.log('✅ Loaded activities:', activitiesData.length);
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      // Check file size (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        alert(`File too large. Maximum size is 100MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        return;
      }
      
      setSelectedFile(file);
      setExtractedActivities([]);
      setShowExtractedPreview(false);
    } else {
      alert('Please select a valid PDF file');
    }
  };

  const extractActivitiesFromPDF = async () => {
    if (!selectedFile || !user) return;

    try {
      setIsExtracting(true);
      setUploadProgress(0);
      
      console.log('🔄 Starting PDF activity extraction...');
      
      // Step 1: Extract text from PDF (now returns pages)
      setUploadProgress(25);
      const { text, pages, numPages } = await pdfActivityService.extractPDFText(selectedFile);
      console.log('📄 PDF extracted:', { numPages: numPages || (pages ? pages.length : 'unknown') });
      
      // Step 2: Parse activities by pages (1 activity per page, except activity 3 spans 2 pages)
      setUploadProgress(50);
      let activities = [];
      if (pages && pages.length) {
        activities = pdfActivityService.parseActivitiesByPages(pages);
      } else {
        activities = pdfActivityService.parseNumberedActivities(text);
      }
      console.log('📋 Activities parsed:', activities.length);
      
      setExtractedActivities(activities);
      setShowExtractedPreview(true);
      setUploadProgress(100);
      
      console.log('✅ PDF activity extraction completed');
      
    } catch (error) {
      console.error('Error extracting activities from PDF:', error);
      alert(`Error extracting activities from PDF: ${error.message}`);
    } finally {
      setIsExtracting(false);
      setUploadProgress(0);
    }
  };

  const uploadExtractedActivities = async () => {
    if (!selectedFile || !user || extractedActivities.length === 0) {
      console.log('❌ Upload blocked:', { 
        hasFile: !!selectedFile, 
        hasUser: !!user, 
        hasActivities: extractedActivities.length > 0,
        userDetails: user ? { uid: user.uid, email: user.email } : null
      });
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      console.log('🔄 Starting activity upload to Firebase...');
      console.log('👤 User details:', { uid: user.uid, email: user.email });
      
      // Upload activities to Firestore
      setUploadProgress(50);
      const uploadResult = await pdfActivityService.storeActivitiesInFirestore(extractedActivities);
      console.log('✅ Activities uploaded to Firestore:', uploadResult);
      
      // Upload PDF to storage
      setUploadProgress(75);
      const pdfUploadResult = await pdfActivityService.uploadActivityPDF(selectedFile, user.uid);
      console.log('📁 PDF uploaded to storage:', pdfUploadResult);
      
      setUploadProgress(100);
      
      alert(`Successfully extracted and uploaded ${extractedActivities.length} activities from PDF!`);
      
      // Reset state
      setSelectedFile(null);
      setExtractedActivities([]);
      setShowExtractedPreview(false);
      
      // Reload activities
      await loadActivities();
      
    } catch (error) {
      console.error('Error uploading activities:', error);
      alert('Error uploading activities. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const uploadPDF = async () => {
    if (!selectedFile || !user) return;

    try {
      setIsUploading(true);
      const result = await pdfActivityService.uploadActivityPDF(selectedFile, user.uid);
      alert(`PDF uploaded successfully! URL: ${result.url}`);
      setSelectedFile(null);
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Error uploading PDF. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const addNewActivity = () => {
    const newActivity = {
      id: activities.length + 1
    };
    setEditingActivity(newActivity);
    setShowAddForm(true);
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setShowAddForm(true);
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    
    try {
      await pdfActivityService.deleteActivity(activityId);
      await loadActivities();
      alert('Activity deleted successfully');
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Error deleting activity');
    }
  };

  const saveActivity = async (activityData) => {
    try {
      if (editingActivity.id) {
        await pdfActivityService.updateActivity(editingActivity.id, activityData);
      } else {
        await pdfActivityService.storeActivitiesInFirestore([activityData]);
      }
      setShowAddForm(false);
      setEditingActivity(null);
      await loadActivities();
      alert('Activity saved successfully');
    } catch (error) {
      console.error('Error saving activity:', error);
      alert('Error saving activity');
    }
  };

    return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Activity Manager</h1>
            <p className="text-gray-600">Manage daily activities and PDF uploads</p>
          </div>
          <BackButton />
        </div>

        {/* Authentication Status */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">Authentication Status</h3>
          <div className="text-sm text-blue-700 space-y-1">
            <p><strong>User:</strong> {user ? user.email : 'Not logged in'}</p>
            <p><strong>UID:</strong> {user ? user.uid : 'N/A'}</p>
            <p><strong>Authenticated:</strong> {isAuthenticated ? 'Yes' : 'No'}</p>
      </div>
          <button
            onClick={async () => {
              try {
                await pdfActivityService.testFirebaseConnection();
                alert('Firebase connection test passed!');
              } catch (error) {
                alert(`Firebase test failed: ${error.message}`);
              }
            }}
            className="mt-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
          >
            Test Firebase Connection
          </button>
        </div>

        {/* PDF Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaFilePdf className="mr-2 text-red-500" />
            PDF Activity Extraction
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select PDF File (with numbered activities)
              </label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            {selectedFile && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={extractActivitiesFromPDF}
                disabled={!selectedFile || isExtracting}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaEye className="mr-2" />
                {isExtracting ? 'Extracting...' : 'Extract Activities'}
              </button>

              {extractedActivities.length > 0 && (
            <button
                  onClick={uploadExtractedActivities}
                  disabled={isUploading}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaUpload className="mr-2" />
                  {isUploading ? 'Uploading...' : `Upload ${extractedActivities.length} Activities`}
            </button>
              )}
            </div>

            {/* Progress Bar */}
            {(isExtracting || isUploading) && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}
          </div>
        </div>

        {/* Extracted Activities Preview */}
        {showExtractedPreview && extractedActivities.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Extracted Activities Preview ({extractedActivities.length} found)
            </h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {extractedActivities.map((activity, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-800">
                      Activity {activity.number}: {activity.title}
                    </h4>
                    <span className="text-sm text-gray-500">#{activity.number}</span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {activity.description.substring(0, 200)}...
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activities List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Current Activities</h2>
            <button
              onClick={addNewActivity}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <FaPlus className="mr-2" />
              Add Activity
            </button>
          </div>

          {activities.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No activities found. Upload a PDF or add activities manually.</p>
          ) : (
          <div className="space-y-4">
            {activities.map((activity) => (
                <div key={activity.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                  <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="font-medium text-gray-800">
                          Activity {activity.number}: {activity.title}
                    </h3>
                        {!activity.isActive && (
                          <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Created: {activity.createdAt ? new Date(activity.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                    <button
                        onClick={() => handleEditActivity(activity)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Edit Activity"
                    >
                      <FaEdit />
                    </button>
                    <button
                        onClick={() => handleDeleteActivity(activity.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete Activity"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Add/Edit Activity Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {editingActivity?.id ? 'Edit Activity' : 'Add New Activity'}
              </h3>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                saveActivity({
                  number: parseInt(formData.get('number')),
                  title: formData.get('title'),
                  description: formData.get('description'),
                  type: 'daily_activity',
                  isActive: true
                });
              }}>
              <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Activity Number
                    </label>
                  <input
                      type="number"
                      name="number"
                      defaultValue={editingActivity?.number || ''}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      defaultValue={editingActivity?.title || ''}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      defaultValue={editingActivity?.description || ''}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex space-x-4 mt-6">
                <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Save
                </button>
                <button
                    type="button"
                  onClick={() => {
                      setShowAddForm(false);
                    setEditingActivity(null);
                  }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivityManager;
